import { InfoOutlined } from '@mui/icons-material'
import {
  Box,
  Chip,
  FormControl,
  FormControlProps,
  FormHelperText,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import {
  FocusEvent,
  Fragment,
  memo,
  MouseEvent,
  ReactNode,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react'
import Select, {
  GroupBase,
  OnChangeValue,
  Props as DefaultProps,
  Theme as ReactSelectTheme,
} from 'react-select'

import { MyInputLabel } from '../MyInputs/MyInputLabel'
import { MuiSelectSize, useMuiSelectStyles } from '../MySelect/selectStyles'

export type DetailConfig<Option> = {
  label: string
  accessor: keyof Option | ((option: Option) => ReactNode)
}

// Ícono SVG minimalista de Información

// ----------------------------------------------------------------------
// 1. COMPONENTE AISLADO: Maneja su propio estado para evitar re-renders
// ----------------------------------------------------------------------
const DetailsTriggerComponent = <OptionType, IsMulti extends boolean>({
  value,
  detailsConfig,
  getOptionLabel,
  getOptionValue,
  size,
  uniqueId,
}: {
  value: OnChangeValue<OptionType, IsMulti>
  detailsConfig?: DetailConfig<OptionType>[]
  getOptionLabel: (option: OptionType) => string
  getOptionValue: (option: OptionType) => string
  size: MuiSelectSize
  uniqueId: string
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handlePopoverOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const isPopoverOpen = Boolean(anchorEl)
  const popoverId = isPopoverOpen ? `details-popover-${uniqueId}` : undefined

  // Función para renderizar la tarjeta o "carta" de cada opción seleccionada
  const renderOptionCard = (opt: OptionType) => {
    const headerValue = getOptionValue(opt)
    const title = getOptionLabel(opt)
    const hasExtraContent = detailsConfig && detailsConfig.length > 0

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
        {/* ENCABEZADO */}
        <Box
          sx={(theme) => ({
            px: 2,
            pt: 1.5,
            pb: 1,
            bgcolor: theme.palette.background.default,
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          })}
        >
          <Typography variant="overline" color="text.secondary" fontWeight="600" sx={{ lineHeight: 1 }}>
            VALOR
          </Typography>
          <Chip
            label={headerValue}
            size="small"
            variant="outlined"
            sx={{ fontFamily: 'monospace', fontWeight: 600, userSelect: 'all' }}
          />
        </Box>

        {/* CUERPO */}
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle2"
            component="div"
            fontWeight="700"
            color="text.primary"
            sx={{ mb: hasExtraContent ? 1.5 : 0, userSelect: 'all' }}
          >
            {title}
          </Typography>

          {/* CAMPOS CONFIGURADOS EN detailsConfig */}
          {hasExtraContent && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                columnGap: 2,
                rowGap: 1,
                alignItems: 'center',
              }}
            >
              {detailsConfig.map((config, index) => (
                <Fragment key={index}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight="500"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {config.label}:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ wordBreak: 'break-word', textAlign: 'right', userSelect: 'all' }}
                  >
                    {typeof config.accessor === 'function'
                      ? config.accessor(opt)
                      : String(opt[config.accessor as keyof OptionType] ?? '-')}
                  </Typography>
                </Fragment>
              ))}
            </Box>
          )}

          {!hasExtraContent && (
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', mt: 1 }}>
              No cuenta con detalles adicionales.
            </Typography>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Tooltip title="Ver detalles" disableInteractive>
        <IconButton
          aria-describedby={popoverId}
          onClick={handlePopoverOpen}
          size="small"
          color={isPopoverOpen ? 'primary' : 'default'}
          sx={{
            mt: size === 'small' ? 0.25 : 0.75,
            flexShrink: 0,
          }}
        >
          <InfoOutlined />
        </IconButton>
      </Tooltip>

      <Popover
        id={popoverId}
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        disableAutoFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 1 }}
        slotProps={{
          paper: {
            elevation: 6,
            sx: (theme) => ({
              borderRadius: 2,
              minWidth: { xs: 250, sm: 290 },
              maxWidth: { xs: 'calc(100vw - 32px)', sm: 320 },
              overflow: 'visible',
              mt: 1,
              border: '1px solid',
              borderColor: theme.palette.divider,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: theme.palette.background.default,
                borderTop: '1px solid',
                borderLeft: '1px solid',
                borderColor: theme.palette.divider,
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            }),
          },
        }}
      >
        {/* Manejo de isMulti (Array) o Single */}
        {Array.isArray(value) ? (
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {(value as OptionType[]).map((opt, i) => (
              <Fragment key={i}>
                {i > 0 && <Box borderTop="2px solid" />}
                {renderOptionCard(opt)}
              </Fragment>
            ))}
          </Box>
        ) : (
          renderOptionCard(value as OptionType)
        )}
      </Popover>
    </>
  )
}

// Memoizamos el trigger para que no se re-renderice si cambia el foco del Select padre
const DetailsTrigger = memo(DetailsTriggerComponent) as typeof DetailsTriggerComponent

export type SelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = DefaultProps<Option, IsMulti, Group> & {
  error?: boolean
  formHelperText?: string
  inputLabel?: string
  formControlProps?: FormControlProps
  size?: MuiSelectSize
  fullWidth?: boolean
  /** Muestra por defecto el Valor y Etiqueta de la opción seleccionada */
  showDetails?: boolean
  /** Configuraciones extras a mostrar en el Popover */
  detailsConfig?: DetailConfig<Option>[]
}

const FormSelectComponent = <
  OptionType,
  IsMulti extends boolean = false,
  Group extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
  props: SelectProps<OptionType, IsMulti, Group>,
) => {
  const theme = useTheme()
  const {
    error = false,
    required = false, // <-- Extraemos la prop con valor por defecto
    formHelperText,
    inputLabel,
    formControlProps,
    size = 'small',
    onFocus,
    onBlur,
    fullWidth = true,
    placeholder = '-- Seleccione --',
    value,
    defaultValue,
    detailsConfig,
    showDetails = false,
    ...others
  } = props

  const uniqueId = useId()
  const selectInputId = props.inputId || `mui-select-input-${uniqueId}`
  const labelId = `mui-select-label-${uniqueId}`

  // Estado local para el foco
  const [isFocused, setIsFocused] = useState(false)

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)
      if (onFocus) onFocus(event)
    },
    [onFocus],
  )

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      if (onBlur) onBlur(event)
    },
    [onBlur],
  )

  // Configuración del tema interno de react-select usando su tipo nativo
  const reactSelectThemeConfig = useMemo(
    () => (baseTheme: ReactSelectTheme) => ({
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        primary: theme.palette.primary.main,
      },
    }),
    [theme.palette.primary.main],
  )

  const customStyles = useMuiSelectStyles<OptionType, IsMulti, Group>(error, size)

  // Determinamos si hay un valor válido seleccionado para controlar el colapso
  const hasValue = value != null && (Array.isArray(value) ? value.length > 0 : true)
  const shouldShowDetailsBtn = hasValue && (showDetails || (detailsConfig && detailsConfig.length > 0))

  const safeGetOptionLabel = others.getOptionLabel || ((opt: any) => String(opt.label))
  const safeGetOptionValue = others.getOptionValue || ((opt: any) => String(opt.value))

  return (
    <FormControl
      error={error}
      fullWidth={fullWidth}
      variant="outlined"
      focused={isFocused}
      size={size === 'large' ? 'medium' : size}
      {...formControlProps}
      sx={{
        ...formControlProps?.sx,
      }}
    >
      {inputLabel && (
        <MyInputLabel
          id={labelId}
          htmlFor={selectInputId}
          shrink={true}
          focused={isFocused}
          error={error}
          required={required}
        >
          {inputLabel}
        </MyInputLabel>
      )}
      <Box display="flex" alignItems="flex-start" gap={1} width="100%">
        <Box flex={1} minWidth={0}>
          <Select
            placeholder={placeholder}
            {...others}
            value={value}
            defaultValue={defaultValue}
            inputId={selectInputId}
            styles={customStyles}
            onFocus={handleFocus}
            onBlur={handleBlur}
            theme={reactSelectThemeConfig}
            menuPosition={'fixed'}
          />
        </Box>

        {/* Usamos el nuevo sub-componente aislado */}
        {shouldShowDetailsBtn && (
          <DetailsTrigger
            value={value as OnChangeValue<OptionType, IsMulti>}
            detailsConfig={detailsConfig}
            getOptionLabel={safeGetOptionLabel}
            getOptionValue={safeGetOptionValue}
            size={size}
            uniqueId={uniqueId}
          />
        )}
      </Box>

      {formHelperText && (
        <FormHelperText error={error} sx={{ mx: 0, mt: 0.5 }}>
          {formHelperText}
        </FormHelperText>
      )}
    </FormControl>
  )
}

/**
 * Componente Select para formularios con integración visual de Material UI.
 * Incluye soporte incorporado para renderizar detalles mediante un Popover aislado.
 * Si no desea enviar ARTICULO_DETAILS_CONFIG, puede enviar shoDetails=true, este renderizar el popover con la información del key, value
 *
 * ⚠️ **IMPORTANTE (RENDIMIENTO):** Al estar optimizado con `React.memo`, **NO pases referencias
 * en línea** (funciones flecha anónimas o arreglos `[]`) directamente en los props para
 * evitar re-renders infinitos.
 *
 * @example
 * ```tsx
 * // 1. EXTRAER CONFIGURACIONES Y FUNCIONES PURAS FUERA DEL COMPONENTE
 * const ARTICULO_DETAILS_CONFIG: DetailConfig<ArticuloProps>[] = [
 *   { label: 'Codigo', accessor: 'codigoArticulo' }
 * ];
 * const getArticuloOptionValue = (val: ArticuloProps) => val._id;
 * const getArticuloOptionLabel = (val: ArticuloProps) => val.nombreArticulo;
 *
 * export const ApArticuloForm = () => {
 *   const { control, setValue } = useFormContext();
 *
 *   // 2. MEMOIZAR EL ONCHANGE, evita re-renders
 *   const handleArticuloChange = useCallback((value: ArticuloProps | null, onChangeField: any) => {
 *     onChangeField(value);
 *     setValue('unidadMedidaSimple', null);
 *   }, [setValue]);
 *
 *   return (
 *     <Controller
 *       control={control}
 *       name="articulo"
 *       render={({ field, fieldState: { error } }) => (
 *         <FormSelect<ArticuloProps>
 *           options={articulos}
 *           value={field.value}
 *           onChange={(val) => handleArticuloChange(val, field.onChange)}
 *           getOptionValue={getArticuloOptionValue}
 *           getOptionLabel={getArticuloOptionLabel}
 *           shoDetails={true}
 *           detailsConfig={ARTICULO_DETAILS_CONFIG}
 *           error={!!error}
 *           formHelperText={error?.message}
 *           isClearable={true}
 *         />
 *       )}
 *     />
 *   );
 * };
 * ```
 */
const FormSelect = memo(FormSelectComponent) as typeof FormSelectComponent
export default FormSelect
